import { Injectable, computed, inject, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { BehaviorSubject, filter, firstValueFrom, map, Observable, take } from "rxjs";

import { ApiSessionService } from "../../api-session.service";
import { ResourceState, initialResourceState } from "../../resource-state";
import {
  ApiAddAction,
  ApiReadAction,
  ApiDeleteAction,
  ApiEditAction,
  ApiBrowseAction,
  ApiAction
} from "../../api-actions";
import { ApiUser, User, apiUserToUser as clean } from "./user";
export { User };
import { extractData } from "../../apiResponse/response-body-new";
import {
  IApiError as ApiError,
  genericApiAction,
  onsipApiBrowse
} from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { ParameterValue } from "../../util/api-action-description";
import { isAtLeastAccountAdminRole } from "../../role";
import { ApiStateStoreService } from "../../api-state-store.service";
import { domain } from "../../apiParams/domain";

@Injectable({ providedIn: "root" })
export class UserService {
  protected readonly session = inject(ApiSessionService);
  private readonly store = inject(ApiStateStoreService);

  static readonly usersById = new BehaviorSubject<Record<string, User>>({});

  private readonly _state = signal<ResourceState<User>>(initialResourceState<User>());

  constructor() {
    toObservable(this._state).subscribe(s => UserService.usersById.next(s.data));
  }

  readonly state = this._state.asReadonly();
  readonly users = computed((): Array<User> => Object.values(this._state().data));
  readonly orgUsers = computed((): Array<User> => {
    const orgId = this.session.organizationId();
    return this.users().filter(u => u.organizationId === orgId);
  });

  private readonly _orgUsers$ = toObservable(this.orgUsers).pipe(
    filter(() => !this._state().loading)
  );
  private readonly _selfUser$ = toObservable(
    computed((): User | undefined => {
      const uid = this.session.userId();
      return uid ? this._state().data[uid] : undefined;
    })
  ).pipe(filter((u): u is User => !!u));

  get orgState(): Observable<Array<User>> {
    return this._orgUsers$;
  }

  get selfUser(): Observable<User> {
    return this._selfUser$;
  }

  get selfUserIsAccountAdmin$(): Observable<boolean> {
    return this._selfUser$.pipe(map(user => isAtLeastAccountAdminRole(user.roles)));
  }

  dispose(): void {
    this._state.set(initialResourceState<User>());
  }

  async userAdd(extraParameters?: Record<string, ParameterValue>): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const Domain = await firstValueFrom(this.store.state.pipe(domain(), take(1)));
      const response = await genericApiAction({
        Action: ApiAddAction.UserAdd,
        SessionId: this.session.sessionId(),
        OrganizationId: this.session.organizationId(),
        Domain,
        ...extraParameters
      });
      const user = clean(extractData<ApiUser>(response, ApiAddAction.UserAdd, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userDelete(
    UserId: string,
    extraParameters: Record<string, ParameterValue> = {}
  ): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      await genericApiAction({
        Action: ApiDeleteAction.UserDelete,
        SessionId: this.session.sessionId(),
        UserId,
        ...extraParameters
      });
      this._state.update(s => {
        const data = { ...s.data };
        delete data[UserId];
        return { ...s, loading: false, data };
      });
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditContact(extraParameters?: Record<string, ParameterValue>): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditContact,
        SessionId: this.session.sessionId(),
        ...extraParameters
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditContact, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditPSTNTrunking(
    Enabled: boolean,
    UserId: string,
    extraParameters?: Record<string, ParameterValue>
  ): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditPSTNTrunking,
        Enabled,
        SessionId: this.session.sessionId(),
        UserId,
        ...extraParameters
      });
      const user = clean(
        extractData<ApiUser>(response, ApiEditAction.UserEditPSTNTrunking, "User")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditQueueEvent(
    Enabled: boolean,
    UserId: string,
    extraParameters?: Record<string, ParameterValue>
  ): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditQueueEvent,
        SessionId: this.session.sessionId(),
        UserId,
        Enabled,
        ...extraParameters
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditQueueEvent, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditTeamPageVisibility(
    UserId: string,
    Enabled: boolean,
    extraParameters?: Record<string, ParameterValue>
  ): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditTeamPageVisibility,
        Enabled,
        SessionId: this.session.sessionId(),
        UserId,
        ...extraParameters
      });
      const user = clean(
        extractData<ApiUser>(response, ApiEditAction.UserEditTeamPageVisibility, "User")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditRoleSubmit(extraParameters?: Record<string, ParameterValue>): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditRoleSubmit,
        SessionId: this.session.sessionId(),
        ...extraParameters
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditRoleSubmit, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditNoOutgoingPstn(extraParameters?: Record<string, ParameterValue>): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditNoOutgoingPstn,
        SessionId: this.session.sessionId(),
        ...extraParameters
      });
      const user = clean(
        extractData<ApiUser>(response, ApiEditAction.UserEditNoOutgoingPstn, "User")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditE911Provisioning(params: { Enabled: boolean; UserId: string }): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditE911Provisioning,
        SessionId: this.session.sessionId(),
        ...params
      });
      const user = clean(
        extractData<ApiUser>(response, ApiEditAction.UserEditE911Provisioning, "User")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditExposePii(params: { Enabled: boolean; UserId?: string }): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditExposePii,
        SessionId: this.session.sessionId(),
        UserId: this.session.userId(),
        ...params
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditExposePii, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userRead(extraParameters?: Record<string, ParameterValue>): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiReadAction.UserRead,
        SessionId: this.session.sessionId(),
        ...extraParameters
      });
      const user = clean(extractData<ApiUser>(response, ApiReadAction.UserRead, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userBrowse(extraParameters?: Record<string, ParameterValue>): Promise<Array<User>> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const pages = await onsipApiBrowse({
        Action: ApiBrowseAction.UserBrowse,
        SessionId: this.session.sessionId(),
        OrganizationId: this.session.organizationId(),
        ...extraParameters
      });
      const items = pages.flatMap(page =>
        extractData<Array<ApiUser>>(page, ApiBrowseAction.UserBrowse, "User", "Users").map(clean)
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, ...arrayToRecord(items, "userId") }
      }));
      return items;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAckHostedE911(): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      await genericApiAction({
        Action: ApiAction.UserAckHostedE911,
        SessionId: this.session.sessionId()
      });
      this._state.update(s => ({ ...s, loading: false }));
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAckHostedTerms(): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      await genericApiAction({
        Action: ApiAction.UserAckHostedTerms,
        SessionId: this.session.sessionId()
      });
      this._state.update(s => ({ ...s, loading: false }));
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditStatus(params: { UserId: string; Status: "enabled" | "disabled" }): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditStatus,
        SessionId: this.session.sessionId(),
        ...params
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditStatus, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userEditMfaEmail(params: { UserId?: string; Enabled: boolean }): Promise<User> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserEditMfaEmail,
        SessionId: this.session.sessionId(),
        UserId: this.session.userId(),
        ...params
      });
      const user = clean(extractData<ApiUser>(response, ApiEditAction.UserEditMfaEmail, "User"));
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [user.userId]: user }
      }));
      return user;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userResetPortalPassword(UserId: string): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      await genericApiAction({
        Action: ApiAction.UserResetPortalPassword,
        SessionId: this.session.sessionId(),
        UserId
      });
      this._state.update(s => ({ ...s, loading: false }));
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }
}
